import { useSearchParams } from "react-router-dom";
import { useEffect, useState, ReactNode } from "react";
import { Helmet } from "react-helmet-async";

type StatusType = "confirmed" | "declined" | "already_confirmed" | "already_declined" | "expired" | "not_found" | "unauthorized" | "invalid" | "error";

interface BookingInfo {
  name: string;
  date: string;
  time: string;
  guests: string;
}

const statusConfig: Record<StatusType, { title: string; icon: string; iconBg: string; iconBorder: string; iconGlow: string; renderMessage: (name?: string) => ReactNode; badgeText?: string; badgeColor?: string }> = {
  confirmed: {
    title: "Bokning Godkänd",
    icon: "✔️",
    iconBg: "rgba(34,197,94,0.07)",
    iconBorder: "rgba(34,197,94,0.2)",
    iconGlow: "rgba(34,197,94,0.13)",
    renderMessage: (name) => <>Bokningen har bekräftats. En bekräftelse har skickats till {name ? <strong>{name}</strong> : "gästen"}.</>,
    badgeText: "✔ Bekräftad",
    badgeColor: "#22c55e",
  },
  declined: {
    title: "Bokning Nekad",
    icon: "✖",
    iconBg: "rgba(239,68,68,0.07)",
    iconBorder: "rgba(239,68,68,0.2)",
    iconGlow: "rgba(239,68,68,0.13)",
    renderMessage: (name) => <>Bokningen har nekats. {name ? <strong>{name}</strong> : "Gästen"} har informerats.</>,
    badgeText: "✖ Nekad",
    badgeColor: "#ef4444",
  },
  already_confirmed: {
    title: "Redan behandlad",
    icon: "✅",
    iconBg: "rgba(245,158,11,0.07)",
    iconBorder: "rgba(245,158,11,0.2)",
    iconGlow: "rgba(245,158,11,0.13)",
    renderMessage: () => <>Du har redan <strong>godkänt</strong> denna bokning. Inget mer behöver göras.</>,
    badgeText: "✔ Godkänd",
    badgeColor: "#22c55e",
  },
  already_declined: {
    title: "Redan behandlad",
    icon: "✅",
    iconBg: "rgba(245,158,11,0.07)",
    iconBorder: "rgba(245,158,11,0.2)",
    iconGlow: "rgba(245,158,11,0.13)",
    renderMessage: () => <>Du har redan <strong>nekat</strong> denna bokning. Inget mer behöver göras.</>,
    badgeText: "✖ Nekad",
    badgeColor: "#ef4444",
  },
  expired: {
    title: "Länken har gått ut",
    icon: "⏳",
    iconBg: "rgba(245,158,11,0.07)",
    iconBorder: "rgba(245,158,11,0.2)",
    iconGlow: "rgba(245,158,11,0.13)",
    renderMessage: () => <>Denna länk har gått ut efter 24 timmar. Kontakta gästen direkt.</>,
  },
  not_found: {
    title: "Bokning ej hittad",
    icon: "🔍",
    iconBg: "rgba(239,68,68,0.07)",
    iconBorder: "rgba(239,68,68,0.2)",
    iconGlow: "rgba(239,68,68,0.13)",
    renderMessage: () => <>Denna bokning finns inte i systemet.</>,
  },
  unauthorized: {
    title: "Obehörig åtkomst",
    icon: "🔒",
    iconBg: "rgba(239,68,68,0.07)",
    iconBorder: "rgba(239,68,68,0.2)",
    iconGlow: "rgba(239,68,68,0.13)",
    renderMessage: () => <>Säkerhetstoken matchar inte.</>,
  },
  invalid: {
    title: "Ogiltig länk",
    icon: "🔗",
    iconBg: "rgba(239,68,68,0.07)",
    iconBorder: "rgba(239,68,68,0.2)",
    iconGlow: "rgba(239,68,68,0.13)",
    renderMessage: () => <>Länken saknar nödvändiga parametrar.</>,
  },
  error: {
    title: "Serverfel",
    icon: "⚠️",
    iconBg: "rgba(239,68,68,0.07)",
    iconBorder: "rgba(239,68,68,0.2)",
    iconGlow: "rgba(239,68,68,0.13)",
    renderMessage: () => <>Ett oväntat fel uppstod. Försök igen senare.</>,
  },
};

export default function BookingResponse() {
  const [searchParams] = useSearchParams();
  const [visible, setVisible] = useState(false);

  const status = (searchParams.get("status") || "error") as StatusType;
  const config = statusConfig[status] || statusConfig.error;

  const booking: BookingInfo | null = searchParams.get("name")
    ? {
        name: searchParams.get("name") || "",
        date: searchParams.get("date") || "",
        time: searchParams.get("time") || "",
        guests: searchParams.get("guests") || "",
      }
    : null;

  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Inter:wght@300;400;500;600&display=swap";
    document.head.appendChild(link);
    requestAnimationFrame(() => setVisible(true));
    return () => { document.head.removeChild(link); };
  }, []);

  return (
    <>
      <Helmet>
        <title>Bokningssvar – Currylicious Yum Yum</title>
        <meta name="description" content="Status för din bordsbokning hos Currylicious Yum Yum." />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://www.currylicious.se/booking-response" />
        <meta property="og:title" content="Bokningssvar – Currylicious Yum Yum" />
        <meta property="og:description" content="Status för din bordsbokning hos Currylicious Yum Yum." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.currylicious.se/booking-response" />
      </Helmet>
      <div
      style={{
        fontFamily: '"Inter", sans-serif',
        background: "#0a0a0a",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        padding: 20,
        backgroundImage:
          "radial-gradient(ellipse at top, rgba(212,168,75,0.06) 0%, transparent 60%), radial-gradient(ellipse at bottom, rgba(212,168,75,0.03) 0%, transparent 60%)",
      }}
    >
      <div
        style={{
          maxWidth: 520,
          width: "100%",
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(30px)",
          transition: "opacity 0.8s ease-out, transform 0.8s ease-out",
        }}
      >
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div
            style={{
              fontFamily: '"Playfair Display", serif',
              fontSize: 28,
              fontWeight: 700,
              background: "linear-gradient(135deg, #d4a84b, #f0d78c, #d4a84b)",
              backgroundSize: "200% auto",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              letterSpacing: 3,
              textTransform: "uppercase" as const,
            }}
          >
            Currylicious
          </div>
          <div
            style={{
              fontFamily: '"Inter", sans-serif',
              fontSize: 11,
              color: "rgba(212,168,75,0.5)",
              letterSpacing: 4,
              textTransform: "uppercase" as const,
              marginTop: 4,
            }}
          >
            Restaurant
          </div>
        </div>

        {/* Card */}
        <div
          style={{
            background: "linear-gradient(145deg, #151515, #1a1a1a)",
            borderRadius: 20,
            padding: "48px 40px",
            textAlign: "center",
            border: "1px solid rgba(212,168,75,0.2)",
            boxShadow: "0 25px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(212,168,75,0.05)",
            position: "relative" as const,
            overflow: "hidden",
          }}
        >
          {/* Top gold line */}
          <div
            style={{
              position: "absolute" as const,
              top: 0,
              left: 0,
              right: 0,
              height: 1,
              background: "linear-gradient(90deg, transparent, rgba(212,168,75,0.5), transparent)",
            }}
          />

          {/* Icon */}
          <div
            style={{
              width: 88,
              height: 88,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 24px",
              fontSize: 44,
              background: config.iconBg,
              border: `2px solid ${config.iconBorder}`,
              boxShadow: `0 0 30px ${config.iconGlow}`,
              opacity: visible ? 1 : 0,
              transform: visible ? "scale(1)" : "scale(0.5)",
              transition: "opacity 0.6s ease-out 0.3s, transform 0.6s ease-out 0.3s",
            }}
          >
            {config.icon}
          </div>

          {/* Title */}
          <h1
            style={{
              fontFamily: '"Playfair Display", serif',
              fontSize: 26,
              fontWeight: 700,
              marginBottom: 14,
              background: "linear-gradient(135deg, #d4a84b, #f0d78c)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              opacity: visible ? 1 : 0,
              transform: visible ? "translateY(0)" : "translateY(20px)",
              transition: "opacity 0.7s ease-out 0.4s, transform 0.7s ease-out 0.4s",
            }}
          >
            {config.title}
          </h1>

          {/* Message */}
          <p
            style={{
              color: "#b0b0b0",
              fontSize: 15,
              lineHeight: 1.7,
              opacity: visible ? 1 : 0,
              transform: visible ? "translateY(0)" : "translateY(20px)",
              transition: "opacity 0.7s ease-out 0.5s, transform 0.7s ease-out 0.5s",
            }}
          >
            {config.renderMessage(booking?.name)}
          </p>

          {/* Booking details */}
          {booking && (
            <div
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(20px)",
                transition: "opacity 0.7s ease-out 0.6s, transform 0.7s ease-out 0.6s",
              }}
            >
              <div
                style={{
                  height: 1,
                  background: "linear-gradient(90deg, transparent, rgba(212,168,75,0.25), transparent)",
                  margin: "24px 0",
                }}
              />
              <table style={{ width: "100%", borderCollapse: "collapse" as const }}>
                <tbody>
                  {[
                    { icon: "👤", label: "Gäst", value: booking.name },
                    { icon: "📅", label: "Datum", value: booking.date },
                    { icon: "🕐", label: "Tid", value: booking.time },
                    { icon: "👥", label: "Gäster", value: `${booking.guests} st` },
                  ].map((row) => (
                    <tr key={row.label}>
                      <td
                        style={{
                          padding: "12px 0",
                          borderBottom: "1px solid rgba(255,255,255,0.06)",
                          color: "#d4a84b",
                          fontWeight: 600,
                          textAlign: "left" as const,
                          width: "45%",
                          fontSize: 13,
                          letterSpacing: 0.5,
                          textTransform: "uppercase" as const,
                        }}
                      >
                        {row.icon} {row.label}
                      </td>
                      <td
                        style={{
                          padding: "12px 0",
                          borderBottom: "1px solid rgba(255,255,255,0.06)",
                          color: "#e8e8e8",
                          textAlign: "right" as const,
                          fontWeight: 400,
                          fontSize: 14,
                        }}
                      >
                        {row.value}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Status badge */}
          {config.badgeText && config.badgeColor && (
            <div
              style={{
                display: "inline-block",
                padding: "7px 20px",
                borderRadius: 24,
                fontSize: 13,
                fontWeight: 600,
                marginTop: 20,
                letterSpacing: 0.5,
                background: `${config.badgeColor}18`,
                color: config.badgeColor,
                border: `1px solid ${config.badgeColor}44`,
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(20px)",
                transition: "opacity 0.7s ease-out 0.7s, transform 0.7s ease-out 0.7s",
              }}
            >
              {config.badgeText}
            </div>
          )}
        </div>
      </div>
      </div>
    </>
  );
}
